import { type FunctionComponent, useCallback, type Ref, memo } from "react";
import classnames from "classnames";

import { AlertType, type BetControlsViewModel } from "./BetControls.types";

import { Option } from "@ppb/the-wall-web/components/walls/Option/Option";
import { FixedNumberInputField } from "@ppb/the-wall-web/components/walls/FixedNumberInputField/FixedNumberInputField";
import { CurrencyNumberInputField } from "@ppb/the-wall-web/components/walls/CurrencyNumberInputField/CurrencyNumberInputField";
import { FreeBetsCardLabel } from "@ppb/the-wall-web/components/bricks/FreeBetsCardLabel/FreeBetsCardLabel";
import { AccaInsurance } from "@ppb/the-wall-web/components/rooms/AccaInsurance/AccaInsurance";
import { Hint } from "@ppb/the-wall-web/components/bricks/Hint/Hint";
import { PromoButton } from "../../../PromoButton/PromoButton.web";
import { PNLAndWhatIf } from "../../../PNLAndWhatIf/PNLAndWhatIf.web";

import styles from "./BetControls.web.css";
import { Alert } from "@ppb/the-wall-web/components/bricks/Alert/Alert";

type ExtraProps = { innerRef?: Ref<HTMLDivElement> };

type LinesProps = Pick<BetControlsViewModel, "dividendBetLabel" | "linesLabel">;

const Lines: FunctionComponent<LinesProps> = ({ linesLabel, dividendBetLabel }) => (
  <div className={styles.lines}>
    {dividendBetLabel && <span className={styles.dividendBet}>{dividendBetLabel}</span>}
    <span className={styles.linesLabel}>{linesLabel}</span>
  </div>
);

export const BetControls: FunctionComponent<BetControlsViewModel & ExtraProps> = memo(
  ({
    innerRef,
    id,
    betType,
    odds,
    oddsLabel,
    currencySymbol,
    stake,
    stakeLabel,
    separator,
    bonusAvailabilityLabel,
    linesLabel,
    dividendBetLabel,
    formattedOriginalPotentialReturns,
    formattedPotentialReturns,
    returnsLabel,
    multiplier,
    oddsMovement,
    oddsMovementUp,
    oddsMovementDown,
    hasEachWay,
    hasAccaInsurance,
    hasGenerosity,
    hasStartingPrice,
    hasBonusBall,
    hasStakeCaret,
    startingPriceTitle = "",
    eachWayTitle = "",
    eachWaySubtitle,
    bonusBallTitle = "",
    bonusBallSubtitle,
    accaInsuranceTitle = "",
    accaInsuranceSubtitle,
    accaInsuranceTermsLabel = "",
    accaInsuranceTermsUrl = "",
    hintMessage,
    hintType,
    previousOdds,
    isAccaInsuranceSelected,
    isAccaInsuranceReadonly,
    isAccaInsuranceDisabled = false,
    isEachWaySelected,
    isEachWayReadonly,
    isEachWayDisabled = false,
    isBonusBallSelected,
    isBonusBallReadonly,
    isBonusBallDisabled = false,
    isPanelDisabled = false,
    isPriceBoostSelected = false,
    isGenerositySelected = false,
    isStartingPriceReadonly = false,
    isStartingPriceSelected,
    isStartingPriceDisabled = false,
    isStakeFocused,
    isStakeReadonly = true,
    isStakeValid,
    generosityAlertMessage,
    generosityAlertRemoveLabel,
    isGenerosityDisabled = false,
    displayReturns = true,
    onAccaInsurancePress,
    onStakeChange,
    onStakeFocus,
    onStakeBlur,
    onEachWayPress,
    onBonusBallPress,
    onGenerosityPress,
    onStartingPricePress,
    onGenerosityRemovePress,
    generosityIconName,
  }) => {
    const onEachWayPressGuard = useCallback(
      (isSelected: boolean) => onEachWayPress && onEachWayPress(isSelected),
      [onEachWayPress],
    );

    const onBonusBallPressGuard = useCallback(
      (isSelected: boolean) => onBonusBallPress && onBonusBallPress(isSelected),
      [onBonusBallPress],
    );

    const onAccaInsurancePressGuard = useCallback(
      (isSelected: boolean) => onAccaInsurancePress && onAccaInsurancePress(isSelected),
      [onAccaInsurancePress],
    );

    const onStartingPricePressGuard = useCallback(
      (isSelected: boolean) => onStartingPricePress && onStartingPricePress(isSelected),
      [onStartingPricePress],
    );

    const betControlsClassnames = classnames(styles.betControls, {
      [styles.disabled]: isPanelDisabled,
    });

    const showHint = hintMessage && hintType;
    const showFreeBetsLabel = !showHint && !!bonusAvailabilityLabel;

    return (
      <div className={betControlsClassnames}>
        {!!betType && <span className={styles.title}>{betType}</span>}
        <div className={styles.controls}>
          <div className={styles.control}>
            {linesLabel ? (
              <Lines linesLabel={linesLabel} dividendBetLabel={dividendBetLabel} />
            ) : (
              <FixedNumberInputField
                previousValue={previousOdds}
                disabled={isPanelDisabled}
                label={oddsLabel}
                value={odds}
                oddsMovement={oddsMovement}
                oddsMovementUp={oddsMovementUp}
                oddsMovementDown={oddsMovementDown}
              />
            )}
          </div>
          <span className={styles.control}>
            <CurrencyNumberInputField
              ref={innerRef}
              id={id}
              disabled={isPanelDisabled}
              readonly={isStakeReadonly}
              label={stakeLabel}
              value={stake}
              separator={separator}
              focused={isStakeFocused}
              invalid={!isStakeValid}
              hasCaret={hasStakeCaret}
              currencySymbol={currencySymbol}
              multiplier={multiplier}
              onChange={onStakeChange}
              onFocus={onStakeFocus}
              onBlur={onStakeBlur}
            />
          </span>
          {hasGenerosity && (
            <PromoButton
              isSelected={isGenerositySelected}
              disabled={isPanelDisabled || isGenerosityDisabled}
              onPress={onGenerosityPress}
              promoIconName={generosityIconName}
            />
          )}
        </div>
        {(!!hasEachWay || !!hasStartingPrice) && (
          <div className={styles.betOptions}>
            {!!hasEachWay && (
              <div className={styles.eachWay}>
                <Option
                  checkboxId={`ewbetcontrols:${id}`}
                  title={eachWayTitle}
                  subtitle={eachWaySubtitle}
                  disabled={isPanelDisabled || isEachWayDisabled}
                  isSelected={!!isEachWaySelected}
                  isReadOnly={isEachWayReadonly}
                  onPress={onEachWayPressGuard}
                />
              </div>
            )}
            {!!hasStartingPrice && (
              <div className={styles.startingPrice}>
                <Option
                  checkboxId={`spcontrols:${id}`}
                  title={startingPriceTitle}
                  isSelected={!!isStartingPriceSelected}
                  disabled={isPanelDisabled || isStartingPriceDisabled}
                  isReadOnly={isStartingPriceReadonly}
                  onPress={onStartingPricePressGuard}
                />
              </div>
            )}
          </div>
        )}
        {!!hasBonusBall && (
          <div className={styles.bonusBall}>
            <Option
              checkboxId={`bonusballcontrols:${id}`}
              title={bonusBallTitle}
              subtitle={bonusBallSubtitle}
              disabled={isPanelDisabled || isBonusBallDisabled}
              isSelected={!!isBonusBallSelected}
              isReadOnly={isBonusBallReadonly}
              onPress={onBonusBallPressGuard}
            />
          </div>
        )}
        {hasAccaInsurance && (
          <AccaInsurance
            title={accaInsuranceTitle}
            subtitle={accaInsuranceSubtitle}
            termsLabel={accaInsuranceTermsLabel}
            termsUrl={accaInsuranceTermsUrl}
            isSelected={!!isAccaInsuranceSelected}
            isReadOnly={isAccaInsuranceReadonly}
            disabled={isPanelDisabled || isAccaInsuranceDisabled}
            onPress={onAccaInsurancePressGuard}
          />
        )}
        {!!generosityAlertMessage && (
          <Alert
            message={generosityAlertMessage}
            iconOverload={generosityIconName}
            type={AlertType.Generosity}
            onClose={(!isPanelDisabled && onGenerosityRemovePress) || undefined}
            showCloseIcon={false}
            dismissLabel={(!isPanelDisabled && generosityAlertRemoveLabel) || undefined}
          />
        )}
        <footer className={styles.footer}>
          {showHint && <Hint message={hintMessage} type={hintType} />}
          {showFreeBetsLabel && <FreeBetsCardLabel label={bonusAvailabilityLabel} disabled={isPanelDisabled} />}
          {displayReturns && (
            <div className={styles.footerRowChild}>
              <span className={styles.returns}>{returnsLabel}</span>
              {!!formattedPotentialReturns && (
                <PNLAndWhatIf
                  pnl={formattedPotentialReturns}
                  previousPnl={formattedOriginalPotentialReturns}
                  boosted={!!isPriceBoostSelected}
                  disabled={isPanelDisabled}
                />
              )}
            </div>
          )}
        </footer>
      </div>
    );
  },
);

BetControls.displayName = "BetControls";
